import { useQuery } from "@tanstack/react-query";
import { pagesApi } from "./pages.api";
import { pagesKeys } from "./pages.keys";
import type { GetPagesCountParams, SearchPagesParams } from "./types";

// Common query options for pages that need fresh data
const FRESH_DATA_OPTIONS = {
  refetchOnMount: "always" as const,
  staleTime: 0,
} as const;

export const useSearchPagesQuery = (
  params: SearchPagesParams,
  options?: { skip?: boolean },
) => {
  return useQuery({
    queryKey: pagesKeys.list(params),
    queryFn: () => pagesApi.searchPages(params),
    ...FRESH_DATA_OPTIONS,
    enabled: !options?.skip,
  });
};

export const useGetPageByIdQuery = (id: string) => {
  return useQuery({
    queryKey: pagesKeys.detail(id),
    queryFn: () => pagesApi.getPageById(id),
    enabled: !!id,
    ...FRESH_DATA_OPTIONS,
  });
};

export const useImportedStoriesQuery = () => {
  return useQuery({
    queryKey: pagesKeys.imported(),
    queryFn: () => pagesApi.getImportedPages(),
    ...FRESH_DATA_OPTIONS,
  });
};

export const useGetPagesWithStoryBrainQuery = (currentStoryId: string) => {
  return useQuery({
    queryKey: pagesKeys.getPagesWithStoryBrain(currentStoryId),
    queryFn: () => pagesApi.getPagesWithStoryBrain(currentStoryId),
    refetchOnMount: "always",
  });
};

export const useGetPagesCountQuery = (
  params: GetPagesCountParams,
  options?: { skip?: boolean },
) => {
  return useQuery({
    queryKey: pagesKeys.getPagesCount(params),
    queryFn: () => pagesApi.getPagesCount(params),
    ...FRESH_DATA_OPTIONS,
    enabled: !options?.skip,
  });
};

export const useGetAccessibleLivePagesQuery = (folderId?: string) => {
  return useQuery({
    queryKey: pagesKeys.getAccessibleLivePages(folderId),
    queryFn: () => pagesApi.getAccessibleLivePages(folderId),
    // Use a small staleTime to allow React Query to properly deduplicate requests
    // When multiple components mount with the same query key, React Query will
    // deduplicate if data exists and is still fresh (within staleTime)
    // staleTime: 1000, // Consider data fresh for 1 second to allow deduplication
    // refetchOnMount: true, // Default behavior - will respect pending requests
    // gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
};
